import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { SimilarStructure } from '../../../../entry/structure-data.models';
import { ConfigService } from '../../../../config.service';
import { CommonService } from '../../../../common.service';
import { GoogleAnalyticsService } from '../../../../google-analytics.service';
import { HttpResponse } from '@angular/common/http';
import { CheckboxService } from '../checkbox.service';
import { Subscription } from 'rxjs';
import { FoldseekService } from '../../../../foldseek-table/foldseek.service';
import { Sort } from '@angular/material/sort';

@Component({
  selector: 'mb-vad-tab',
  templateUrl: './mb-vad-tab.component.html',
  styleUrls: ['./mb-vad-tab.component.css', '../mb-foldseek-table.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class MbVadTabComponent implements OnInit, OnDestroy {
  @Input() accession: string;
  @Input() afdb_accession: string;
  @Output() toggleClickedEvent: EventEmitter<Object> = new EventEmitter();

  displayedColumnsVADStructure: string[] = ['expand', 'alighin3D', 'afdbAccessions', 'speciesNames', 'sequenceLength', 'evalue', 'sequenceIdentity', 'averagePlddt'];
  vadStructureDataSource = new MatTableDataSource<SimilarStructure>();
  isVADLoading = false;
  vadZeroResultsMsg = "";
  errorVAD = "";
  isDatabaseErrorVAD = false;
  startVAD = "0";
  recordsVAD = "10";
  currentVADPage = 0;
  sort_direction_VAD = 'asc';
  sort_column_VAD = 'evalue';
  filteritemVAD = "";
  species_name_filter_VAD = "";
  expandedElementsVAD = [];
  dataLoaded = false;

  checkboxState: { [key: string]: boolean } = {};
  disabledState: { [key: string]: boolean } = {};
  private subscription: Subscription;
  apiData = {};

  constructor(
    private commonService: CommonService,
    private configService: ConfigService,
    public gaService: GoogleAnalyticsService,
    private changeDetectorRef: ChangeDetectorRef,
    private checkboxService: CheckboxService,
    public foldseekService: FoldseekService
  ) {}

  ngOnInit(): void {
    this.subscription = this.checkboxService.checkboxState$.subscribe(state => {
      this.checkboxState = state;
      this.changeDetectorRef.detectChanges();
    });
    this.checkboxService.disabledState$.subscribe(state => {
      this.disabledState = state;
    });
    this.dataLoaded = true;
    this.loadData(this.recordsVAD, this.startVAD, this.sort_direction_VAD, this.sort_column_VAD, this.species_name_filter_VAD);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  clickToggleSuperpose(event, accession) {
    this.toggleClickedEvent.emit({ event, accession, apidata: this.apiData[accession] });
  }

  toggleRowVAD(element) {
    const ele = this.expandedElementsVAD.find(e => e.afdbAccessions === element.afdbAccessions);
    if (ele) {
      this.expandedElementsVAD = this.expandedElementsVAD.filter(el => el.afdbAccessions !== element.afdbAccessions);
    } else {
      this.expandedElementsVAD.push(element);
    }
  }

  isExpandedVAD(element): boolean {
    return !!this.expandedElementsVAD.find(e => e.afdbAccessions === element.afdbAccessions);
  }

  isExpansionDetailRowVAD = (i: number, row) => this.isExpandedVAD(row);

  checkAndLoadData() {
    if (this.dataLoaded) return;
    this.dataLoaded = true;
    this.loadData(this.recordsVAD, this.startVAD, this.sort_direction_VAD, this.sort_column_VAD, this.species_name_filter_VAD);
  }

  loadData(records = "", start = "", sort_direction = 'asc', sort_column = 'evalue', species_name_filter = "") {
    const auth = this.configService.getConfig().apiKey ? `&key=${this.configService.getConfig().apiKey}` : "";
    const configUrl = this.configService.getConfig().searchApiUrl || "https://test.alphafold.ebi.ac.uk/api/search";
    let apiUrl = `${configUrl}?q=${this.accession}&type=structure&foldseek_database=vadb`;
    if (records) apiUrl += `&rows=${records}`;
    if (start) apiUrl += `&start=${start}`;
    if (sort_column && sort_direction) {
      if (sort_column === "evalue") apiUrl += `&sort=${sort_column}+${sort_direction}`;
      if (sort_column === "sequenceIdentity") apiUrl += `&sort=pident+${sort_direction}`;
    }
    if (species_name_filter) apiUrl += `&fq=organismScientificName%3A*${species_name_filter.trim()}*`;

    this.isVADLoading = true;
    this.commonService.getApiDatawithStatus(apiUrl + auth).subscribe(async (response: HttpResponse<any>) => {
      const code = await Number(response.status);
      if (code === 202) {
        if (response.body.message && (response.body.message.startsWith("Foldseek search job is currently running") || response.body.message.startsWith("Initiating new Foldseek search"))) {
          setTimeout(() => {
            if (this.isVADLoading) this.loadData(records, start, sort_direction, sort_column, species_name_filter);
          }, 15000);
        }
      }
      if (code === 200) {
        if (response.body.numFound !== 0) {
          const data = response.body.docs;
          this.foldseekService.updateTotalVAD(response.body.numFound);
          this.errorVAD = "";
          this.vadZeroResultsMsg = "";
          const similarStructureData = this.transformVADStructureData(data);
          this.vadStructureDataSource = new MatTableDataSource<SimilarStructure>(similarStructureData);
        } else {
          this.vadZeroResultsMsg = "No matching structures were found in VAD.";
        }
        this.isVADLoading = false;
        this.changeDetectorRef.detectChanges();
      }
    },
    (error) => {
      const code = error.status;
      this.isVADLoading = false;
      if (code === 404) {
        if (error.error.message === "The search was completed successfully, but no matching results were found") {
          this.vadZeroResultsMsg = "No matching structures were found in VAD.";
        } else if (species_name_filter !== "") {
          this.errorVAD = "No species match your search";
          this.vadZeroResultsMsg = "";
        }
      }
      if (code === 422 || code === 302 || code === 500 || code === 0 || code === 429) {
        this.isDatabaseErrorVAD = true;
      }
      this.changeDetectorRef.detectChanges();
    });
  }

  handlePageEvent(e) {
    const start = (e.pageIndex * e.pageSize).toString();
    this.startVAD = start;
    this.recordsVAD = e.pageSize.toString();
    this.currentVADPage = e.pageIndex;
    this.loadData(this.recordsVAD, start, this.sort_direction_VAD, this.sort_column_VAD, this.filteritemVAD);
    this.gaService.eventEmitter('foldseek_table_action', 'FS_similar_structures', 'click', 'foldseek_paginate', 'Clicks on the paginations in the Foldseek results table');
  }

  sortTable(sortState: Sort) {
    const directions = { "ASC": "DESC", "DESC": "ASC" };
    this.sort_direction_VAD = directions[sortState.direction.toUpperCase()];
    this.sort_column_VAD = sortState.active;
    this.loadData(this.recordsVAD, this.startVAD, this.sort_direction_VAD, this.sort_column_VAD, this.filteritemVAD);
  }

  filterTable(filterby) {
    this.currentVADPage = 0;
    this.filteritemVAD = filterby;
    this.startVAD = "0";
    this.species_name_filter_VAD = filterby;
    this.loadData(this.recordsVAD, "0", this.sort_direction_VAD, this.sort_column_VAD, filterby);
    this.gaService.eventEmitter('foldseek_table_action', 'FS_similar_structures', 'click', 'foldseek_taxfilter', 'Clicks on the "Taxonomy filter" input box on the Foldseek results table');
  }

  clearFilter() {
    this.currentVADPage = 0;
    this.filteritemVAD = "";
    this.species_name_filter_VAD = "";
    this.loadData(this.recordsVAD, "0", this.sort_direction_VAD, this.sort_column_VAD, "");
    this.changeDetectorRef.detectChanges();
  }

  getTitle(accession, reviewedStatus, referenceLabel) {
    if (!accession) return "Unavailable";
    return `${accession}*${referenceLabel}*${reviewedStatus}`;
  }

  getLink(database, target) {
    if (database === "pdb") {
      const [entry, , protein] = target.split("_");
      return protein ?
        `${this.configService.getConfig().pdbeBaseApiUrl}/entry/pdb/${entry}/protein/${protein}` :
        `${this.configService.getConfig().pdbeBaseApiUrl}/entry/pdb/${entry}`;
    }
    return `/entry/${target}`;
  }

  transformVADStructureData(data) {
    const similarClusterData = [];
    if (data) {
      const databaseInfo = {};
      for (let i = 0; i < data.length; i++) {
        similarClusterData.push({
          "refLinkId": this.getLink(data[i].database, data[i].target),
          "afdbAccessions": this.getTitle(data[i].target, data[i].isReviewed, data[i].isReferenceProteome),
          "targetToShow": data[i].modelEntityId,
          "uniprotDescriptions": data[i].uniprotDescription,
          "speciesNames": data[i].organismScientificName ? data[i].organismScientificName : "",
          "sequenceIdentity": data[i].pident ? data[i].pident : "N/A",
          "sequenceLength": data[i].uniprotEnd ? `1-${data[i].uniprotEnd}` : "N/A",
          "evalue": (data[i].evalue != null && data[i].evalue !== "") ? Number(data[i].evalue).toExponential(2) : "N/A",
          "averagePlddt": data[i].globalMetricValue ? data[i].globalMetricValue : "N/A",
          "alighin3D": false,
          "expanded": false,
          "hsps": {
            "current_entry": this.afdb_accession,
            "accession": data[i].target,
            "query_sequence": data[i].qaln,
            "match_sequence": data[i].qaln,
            "match_accession": data[i].taln,
            "qstart": data[i].qstart,
            "qend": data[i].qend,
            "tstart": data[i].tstart,
            "tend": data[i].tend,
            "sequence_stats": [],
            "targetToShow": data[i].entryId,
          }
        });

        data[i].database = "afdb50";
        const foldseekWithDesc = {
          ...data[i],
          targetToShow: data[i].entryId,
          desc: data[i].uniprotDescription,
          refLinkId: this.getLink(data[i].database, data[i].target),
        };
        foldseekWithDesc.tstructure = {
          "url": `https://alphafold.ebi.ac.uk/files/${data[i].modelEntityId}-model_v1.bcif`,
          "format": 'cif',
          "binary": true,
        };
        databaseInfo[data[i].target] = foldseekWithDesc;
      }
      this.apiData = { ...this.apiData, ...databaseInfo };
    }
    return similarClusterData;
  }
}
